/**
 * Analytics Button Component
 * Wrapper for Button component with automatic event tracking
 */

import * as React from "react"
import { Button, type ButtonProps } from "@/shared/widgets/button"
import { useAnalytics } from "../hooks/useAnalytics"
import type { AnalyticsEventProviderType } from "../../application/services/AnalyticsService"

export interface AnalyticsButtonProps extends ButtonProps {
  /**
   * Event name to track when button is clicked
   */
  eventName: string
  /**
   * Additional properties to include with the event
   */
  eventProperties?: Record<string, unknown>
  /**
   * Whether to track the event (useful for conditional tracking)
   * @default true
   */
  trackEvent?: boolean
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[]
}

const AnalyticsButton = React.forwardRef<HTMLButtonElement, AnalyticsButtonProps>(
  ({ eventName, eventProperties, trackEvent = true, eventProviders, onClick, ...props }, ref) => {
    const { analytics, isInitialized } = useAnalytics()

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Track event if enabled and analytics is ready
      if (trackEvent && analytics && isInitialized) {
        try {
          analytics.trackEvent(eventName, eventProperties, { eventProviders })
        } catch (error) {
          console.error(`Error tracking event "${eventName}":`, error)
        }
      }
      
      // Call original onClick if provided
      onClick?.(e)
    }

    return (
      <Button
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    )
  }
)
AnalyticsButton.displayName = "AnalyticsButton"

export { AnalyticsButton }

